import React, {useState, useEffect, useCallback, useRef} from 'react';
import styled from 'styled-components';
import {useNavigate, Routes, Route, useParams, useLocation} from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { DatesSetArg } from '@fullcalendar/core';
import {
    // getAllCardsForUser,
    getCardsForCalendar, // ✨ 새로 만든 API 함수 import
    Card as ApiCard,
    getCardById,
    updateCard,
    deleteCard,
    createCard,
    Board,
    CardList, getBoardList, getListsByBoardId
} from '../api';
import useAuth from '../hooks/useAuth';
import Button from '../components/common/Button';
import CardDetailModal from '../components/board/CardDetailModal';
import Modal from '../components/common/Modal';
import ResponsiveHeader from "../components/common/ResponsiveHeader";

// --- 스타일 정의 ---
const StyledSelect = styled.select`
    width: 100%;
    padding: 0.6rem;
    background-color: var(--bg-input);
    color: var(--text-bright);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 1rem;

    &:focus {
        outline: none;
        border-color: var(--primary-accent);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;

  .highlighted-event {
    animation: shake 10s ease-in-out;
    box-shadow: 0 0 0 4px rgba(255, 255, 0, 0.4);
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    2% { transform: translateX(-2px); }
    4% { transform: translateX(2px); }
    6% { transform: translateX(-2px); }
    8% { transform: translateX(2px); }
    10% { transform: translateX(0); }
  }
`;

const CalendarWrapper = styled.div`
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;
  min-height: 0;

  /* FullCalendar 다크 모드 스타일 */

  .fc {
    color: var(--text-normal);
    --fc-border-color: var(--border-color);
    --fc-today-bg-color: rgba(97, 175, 239, 0.1);
    --fc-event-bg-color: var(--primary-accent);
    --fc-event-border-color: var(--primary-accent);
  }

  .fc .fc-col-header-cell-cushion, .fc .fc-daygrid-day-number {
    color: var(--text-bright);
  }

  .fc .fc-button {
    background-color: var(--bg-list);
    border: 1px solid var(--border-color);
  }

  .fc .fc-button-primary:not(:disabled).fc-button-active, .fc .fc-button-primary:not(:disabled):active {
    background-color: var(--primary-accent);
  }

  .fc-daygrid-more-link {
    color: var(--primary-accent);
    font-weight: bold;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .in-progress {
    border-width: 3px !important;
    border-style: solid !important;
    box-shadow: 0 0 0 1px var(--bg-card);
    animation: rainbow-border 4s linear infinite;
  }

  @keyframes rainbow-border {
    0% {
      border-color: #ffffff;
    }
    17% {
      border-color: #ffdf00;
    }
    33% {
      border-color: #ffffff;
    }
    50% {
      border-color: #ffdf00;
    }
    67% {
      border-color: #ffffff;
    }
    83% {
      border-color: #ffdf00;
    }
    100% {
      border-color: #ffffff;
    }
  }

  .completed-event {
    text-decoration: line-through;
    opacity: 0.6;
  }

  .in-progress-date {
    position: relative;
  }

  .fc-popover {
    max-width: 300px !important;
    width: auto !important;
  }

  .in-progress-date::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 3px solid #FFA500;
    pointer-events: none;
    box-shadow: 0 0 0 1px var(--bg-card);
    animation: rainbow-border 4s linear infinite;
    z-index: 1;
  }

  .fc-popover {
    z-index: 2;
  }
`;
const ModalContent = styled.div`
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;
const ModalTitle = styled.h2`
    font-size: 1.25rem;
    font-weight: bold;
    color: var(--text-bright);
`;
const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0 1.5rem 1.5rem;
    background-color: var(--bg-modal);
`;

// --- 타입 정의 ---
interface CalendarEvent {
    id: string;
    title: string;
    start: string;
    end?: string;
    backgroundColor: string;
    borderColor: string;
    className?: string;
    extendedProps?: {
        status?: string | null;
    };
}

const CalendarPage = () => {
    useAuth();
    const [highlightedCardId, setHighlightedCardId] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const calendarRef = useRef<FullCalendar>(null);
    // ✨ 현재 보고 있는 뷰의 날짜 범위를 저장할 상태 추가
    const [currentDateRange, setCurrentDateRange] = useState<{ start: string; end: string } | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [boards, setBoards] = useState<Board[]>([]);
    const [lists, setLists] = useState<CardList[]>([]);
    const [selectedBoardId, setSelectedBoardId] = useState<string>('');
    const [selectedListId, setSelectedListId] = useState<string>('');


    // ✨ [수정] URL 쿼리 파라미터(cardId) 처리 및 자동 모달 이동
    useEffect(() => {
        // URL에서 카드 ID를 가져옵니다
        const params = new URLSearchParams(location.search);
        const cardId = params.get('cardId');

        if (cardId) {
            setHighlightedCardId(cardId);

            // ✨ 추가된 로직: 쿼리 파라미터에 cardId가 있고, 아직 모달 경로가 아니라면 상세 페이지로 이동
            if (!location.pathname.includes(`/card/${cardId}`)) {
                // 기존 쿼리 파라미터(date 등)를 유지하면서 경로 이동
                navigate(`card/${cardId}${location.search}`, { replace: true });
            }

            const timer = setTimeout(() => {
                setHighlightedCardId(null);
            }, 10000);

            return () => clearTimeout(timer);
        }
    }, [location, navigate]); // navigate 의존성 추가

    // ✨ 변경점: 보드 목록을 불러오는 useEffect 추가
    useEffect(() => {
        const fetchBoards = async () => {
            try {
                const response = await getBoardList();
                const boardList = response.data._embedded?.getList || [];
                setBoards(boardList);
                if (boardList.length > 0) {
                    setSelectedBoardId(boardList[0].id); // 첫 번째 보드를 기본으로 선택
                }
            } catch (error) {
                console.error("Failed to fetch boards", error);
            }
        };
        fetchBoards();
    }, []);

    // ✨ 변경점: 선택된 보드가 바뀔 때마다 리스트 목록을 불러오는 useEffect 추가
    useEffect(() => {
        if (selectedBoardId) {
            const fetchLists = async () => {
                try {
                    const response = await getListsByBoardId(selectedBoardId);
                    const cardLists = response.data._embedded?.getList || [];
                    setLists(cardLists);
                    if (cardLists.length > 0) {
                        setSelectedListId(cardLists[0].id); // 첫 번째 리스트를 기본으로 선택
                    } else {
                        setSelectedListId(''); // 리스트가 없으면 선택 해제
                    }
                } catch (error) {
                    console.error("Failed to fetch lists", error);
                }
            };
            fetchLists();
        }
    }, [selectedBoardId]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const dateParam = queryParams.get('date');
        // date 파라미터와 캘린더 API가 모두 유효할 때
        if (dateParam && calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.gotoDate(dateParam); // 'YYYY-MM-DD' 형식의 날짜로 이동
        }
    }, [location.search]); // URL의 search 부분이 변경될 때마다 실행

    useEffect(() => {
        // 현재 URL 경로가 '/calendar/card/'로 시작하는지 (즉, 모달이 열려 있는지) 확인합니다.
        const isCardModalOpen = location.pathname.startsWith('/calendar/card/');

        if (isCardModalOpen) {
            // FullCalendar의 'more' 링크 클릭 시 나타나는 팝오버 요소를 찾습니다.
            const popover = document.querySelector('.fc-popover');
            // 팝오버가 존재하면 보이지 않도록 스타일을 변경합니다.
            if (popover && popover instanceof HTMLElement) {
                popover.style.display = 'none';
            }
        }
    }, [location.pathname]);

// ✨ [수정] 날짜 포맷 변환 함수
    // FullCalendar의 ISO 스트링에서 타임존 정보를 제거하고 서버 포맷으로 맞춤
    const formatToApiDate = (dateStr: string) => {
        // 1. 타임존 정보(+...)나 UTC 표시(Z)를 기준으로 앞부분만 자릅니다.
        // 예: '2025-11-30T00:00:00+09:00' -> '2025-11-30T00:00:00'
        let formatted = dateStr.split('+')[0].split('Z')[0];

        // 2. 'T'를 공백으로 변경
        formatted = formatted.replace('T', ' ');

        // 3. 날짜만 있는 경우 (yyyy-MM-dd) -> 시간을 00:00:00으로 채움
        if (formatted.length === 10) {
            formatted += ' 00:00:00';
        }

        // 4. 밀리초(.SSS)가 없으면 .000 추가
        // (서버가 .SSS를 필수라고 가정할 때)
        if (!formatted.includes('.')) {
            formatted += '.000';
        }

        return formatted;
    };

    const fetchCardsByRange = useCallback(async (start: string, end: string) => {
        try {
            // 서버 요구 포맷으로 변환
            const apiStart = formatToApiDate(start);
            const apiEnd = formatToApiDate(end);

            // 변환된 날짜로 API 호출
            // 새로 만든 API 호출 (start, end는 ISO string 형태 ex: '2025-01-01T00:00:00...')
            const response = await getCardsForCalendar(apiStart, apiEnd);
            const cards = response.data._embedded?.getList || [];
            const calendarEvents = cards
                .filter(card => card.startDate)
                .map(card => {
                    const endDate = new Date(card.dueDate || card.startDate!);
                    endDate.setDate(endDate.getDate() + 1); // FullCalendar는 end date를 exclusive하게 처리

                    const event: CalendarEvent = {
                        id: card.id,
                        title: card.title,
                        start: card.startDate!.replace(' ', 'T'),
                        backgroundColor: card.labels?.[0]?.color || '#61afef',
                        borderColor: card.status === 'IN_PROGRESS' ? '#FFA500' : (card.labels?.[0]?.color || '#61afef'),
                        className: `${card.id === highlightedCardId ? 'highlighted-event' : ''} ${card.status === 'IN_PROGRESS' ? 'in-progress' : ''} ${card.completed ? 'completed-event' : ''}`,
                        extendedProps: {
                            status: card.status, // Add status here
                        },
                    };

                    if (card.dueDate) {
                        event.end = card.dueDate.replace(' ', 'T');
                    }

                    return event;
                });
            setEvents(calendarEvents);
        } catch (error) {
            console.error("Failed to fetch cards for calendar", error);
        }
    }, [highlightedCardId]);

    // ✨ [변경] FullCalendar의 datesSet 핸들러
    // 캘린더가 렌더링되거나, 월/주가 변경될 때마다 실행됩니다.
    const handleDatesSet = (dateInfo: DatesSetArg) => {
        // 현재 범위 상태 업데이트
        setCurrentDateRange({ start: dateInfo.startStr, end: dateInfo.endStr });
        // API 호출
        // fetchCardsByRange(dateInfo.startStr, dateInfo.endStr);
    };

    // ✨ [변경] 데이터 갱신(refreshKey)이 필요할 때
    // 전체 조회가 아니라 '현재 보고 있는 범위'에 대해서만 다시 조회합니다.
    useEffect(() => {
        if (currentDateRange) {
            fetchCardsByRange(currentDateRange.start, currentDateRange.end);
        }
    }, [refreshKey, fetchCardsByRange, currentDateRange]);
    // 주의: currentDateRange를 의존성에 넣지 않아야 무한 루프를 방지합니다.
    // (datesSet -> state변경 -> effect -> fetch -> state변경...)
    // 하지만 여기서는 refreshKey가 바뀔 때만 실행되어야 하므로 아래처럼 조건을 겁니다.

    // (혹은 더 안전하게 하려면 아래와 같이 ref를 통해 현재 뷰 정보를 가져와도 됩니다)
    /*
    useEffect(() => {
        if (calendarRef.current) {
            const api = calendarRef.current.getApi();
            // 현재 뷰의 날짜 범위를 가져옴
            const { currentStart, currentEnd } = api.view;
            fetchCardsByRange(currentStart.toISOString(), currentEnd.toISOString());
        }
    }, [refreshKey]);
    */

    const handleEventClick = (clickInfo: any) => {
        navigate(`card/${clickInfo.event.id}`);
    };

    // ✨ 변경점: 날짜 클릭 시 모달을 열도록 수정
    const handleDateClick = (arg: { dateStr: string }) => {
        setSelectedDate(arg.dateStr);
        setIsModalOpen(true);
    };

    // ✨ 변경점: 모달에서 카드 생성을 처리하는 함수 추가
    const handleCreateCard = async () => {
        if (!selectedDate || !selectedBoardId || !selectedListId) {
            alert('보드와 리스트를 모두 선택해주세요.');
            return;
        }

        try {
            let finalStartDate: string;

            // 주간/일간 보기에서 시간대를 클릭하면 'T'가 포함된 ISO 형식의 문자열이 반환됩니다.
            if (selectedDate.includes('T')) {
                // 'YYYY-MM-DDTHH:mm:ss' 형식을 API가 요구하는 'YYYY-MM-DD HH:mm:ss'로 변환합니다.
                finalStartDate = selectedDate.replace('T', ' ').substring(0, 19);
            } else {
                // 월간 보기에서 날짜를 클릭하면 'YYYY-MM-DD' 형식만 있으므로 기본 시간을 추가합니다.
                finalStartDate = `${selectedDate} 09:00:00`;
            }

            const response = await createCard({
                title: '새 카드',
                startDate: finalStartDate, // ✨ 변경점: 위에서 처리된 최종 날짜 변수 사용
                boardId: selectedBoardId,
                cardListId: selectedListId,
                priority: 0,
            });

            const newCard = response.data;

            setRefreshKey(prev => prev + 1); // 캘린더 새로고침
            setIsModalOpen(false); // 모달 닫기
            navigate(`card/${newCard.id}`); // 생성된 카드의 상세 모달로 이동
        } catch (error) {
            console.error("Failed to create card", error);
            alert('카드 생성에 실패했습니다.');
        }
    };

    return (
        <PageWrapper>
            <ResponsiveHeader title="Calendar" />
            <CalendarWrapper>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
                    initialView="dayGridMonth"
                    events={events}
                    // ✨ [추가] 뷰가 변하거나 초기 로딩 시 날짜 범위를 알려주는 콜백
                    datesSet={handleDatesSet}
                    eventClick={handleEventClick}
                    dateClick={handleDateClick}
                    selectable={true}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,listWeek'
                    }}
                    buttonText={{
                        prev: '←',
                        next: '→',
                        today: 'Today'
                    }}
                    height="100%"
                    dayMaxEvents={true}
                    dayCellClassNames={(info) => {
                        const hasInProgressEvent = events.some(event => {
                            const eventDate = new Date(event.start);
                            const cellDate = info.date;

                            const isSameDate =
                                eventDate.getFullYear() === cellDate.getFullYear() &&
                                eventDate.getMonth() === cellDate.getMonth() &&
                                eventDate.getDate() === cellDate.getDate();

                            return isSameDate && event.extendedProps?.status === 'IN_PROGRESS';
                        });

                        return hasInProgressEvent ? 'in-progress-date' : '';
                    }}
                />
            </CalendarWrapper>
            <Routes>
                <Route path="card/:cardId" element={
                    <CardRouteWrapper
                        onClose={() => navigate('/calendar')}
                        onUpdateNeeded={() => setRefreshKey(prev => prev + 1)}
                    />}
                />
            </Routes>

            {isModalOpen && (
                <Modal visible={true} onClose={() => setIsModalOpen(false)}>
                    <ModalContent>
                        <ModalTitle>{selectedDate}에 새 카드 추가</ModalTitle>
                        <StyledSelect
                            value={selectedBoardId}
                            onChange={(e) => setSelectedBoardId(e.target.value)}
                        >
                            {boards.map(board => (
                                <option key={board.id} value={board.id}>{board.name}</option>
                            ))}
                        </StyledSelect>
                        <StyledSelect
                            value={selectedListId}
                            onChange={(e) => setSelectedListId(e.target.value)}
                            disabled={lists.length === 0}
                        >
                            {lists.length > 0 ? (
                                lists.map(list => (
                                    <option key={list.id} value={list.id}>{list.name}</option>
                                ))
                            ) : (
                                <option>이 보드에는 리스트가 없습니다.</option>
                            )}
                        </StyledSelect>
                    </ModalContent>
                    <ModalActions>
                        <Button onClick={() => setIsModalOpen(false)} style={{backgroundColor: 'var(--bg-list)'}}>취소</Button>
                        <Button onClick={handleCreateCard} disabled={!selectedListId}>생성</Button>
                    </ModalActions>
                </Modal>
            )}
        </PageWrapper>
    );
};

// 카드 상세 모달을 위한 라우팅 래퍼
const CardRouteWrapper: React.FC<{ onClose: () => void, onUpdateNeeded: () => void }> = ({ onClose, onUpdateNeeded }) => {
    const { cardId } = useParams<{ cardId: string }>();
    const [card, setCard] = useState<ApiCard | null>(null);
    const user = useAuth();
    const isReadOnly = card?.userId !== user?.id && card?.assigneeId !== user?.id; // 담당자는 진행을 고칠 수 있다

    useEffect(() => {
        const fetchCard = async () => {
            if (cardId) {
                try {
                    const response = await getCardById(cardId);
                    setCard(response.data);
                } catch (error) {
                    console.error("Failed to fetch card", error);
                    onClose();
                }
            }
        };
        fetchCard();
    }, [cardId, onClose]);

    if (!card) return null;

    const handleToggleComplete = async () => {
        const originalCard = { ...card };
        try {
            await updateCard({ id: card.id, completed: !card.completed });
            onUpdateNeeded();
            onClose();
        } catch (error: any) {
            console.error("Failed to toggle complete", error);
            setCard(originalCard);
            // 백엔드에서 보낸 에러 메시지 표시
            const message = error.response?.data?.data || "완료 처리 실패";
            alert(message);
        }
    };

    const handleDelete = async () => {
        if(card) {
            await deleteCard(card.id);
            onUpdateNeeded();
            onClose();
        }
    };

    return (
        <CardDetailModal
            card={card}
            onClose={onClose}
            onUpdate={(updatedCard) => {
                setCard(updatedCard);
                onUpdateNeeded();
            }}
            onDelete={handleDelete}
            onToggleComplete={handleToggleComplete}
            fromCalendar={true}
            readOnly={isReadOnly}
        />
    );
};

export default CalendarPage;
